'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, X, AlertCircle, Clock, Ship, ChevronDown, ChevronUp } from 'lucide-react';
import { MOCK_FOLLOW_UP_REPORTS, FollowUpReport, ObservationStatus } from '../../follow-up-reports/components/followUpData';

const STATUS_BAR_COLOR: Record<ObservationStatus, string> = {
  Open: '#dc2626',
  'In Progress': '#d97706',
  Closed: '#16a34a',
};

export default function OpenInspectionsClient() {
  const [search, setSearch] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'openCount' | 'vesselName' | 'inspectionDate'>('openCount');

  // Only show reports with open or in-progress items
  const openReports = useMemo(() => MOCK_FOLLOW_UP_REPORTS.filter((r) => r.openCount > 0 || r.inProgressCount > 0), []);

  const uniqueOwners = Array.from(new Set(openReports.map((r) => r.owner)));
  const uniqueManagers = Array.from(new Set(openReports.map((r) => r.manager)));

  const filtered = useMemo(() => {
    let data = [...openReports];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) => r.vesselName.toLowerCase().includes(q) || r.imoNumber.includes(q) || r.owner.toLowerCase().includes(q)
      );
    }
    if (filterOwner) data = data.filter((r) => r.owner === filterOwner);
    if (filterManager) data = data.filter((r) => r.manager === filterManager);
    if (filterSeverity) {
      data = data.filter((r) =>
        r.observations.some((o) => o.severity === filterSeverity && o.status !== 'Closed')
      );
    }

    data.sort((a, b) => {
      if (sortBy === 'openCount') return (b.openCount + b.inProgressCount) - (a.openCount + a.inProgressCount);
      if (sortBy === 'vesselName') return a.vesselName.localeCompare(b.vesselName);
      if (sortBy === 'inspectionDate') return b.inspectionDate.localeCompare(a.inspectionDate);
      return 0;
    });

    return data;
  }, [openReports, search, filterOwner, filterManager, filterSeverity, sortBy]);

  const summaryStats = useMemo(() => ({
    vessels: filtered.length,
    totalOpen: filtered.reduce((s, r) => s + r.openCount, 0),
    totalInProgress: filtered.reduce((s, r) => s + r.inProgressCount, 0),
    criticalOpen: filtered.reduce(
      (s, r) => s + r.observations.filter((o) => o.severity === 'Critical' && o.status !== 'Closed').length,
      0
    ),
  }), [filtered]);

  const activeFilterCount = [filterOwner, filterManager, filterSeverity].filter(Boolean).length + (search ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setFilterOwner('');
    setFilterManager('');
    setFilterSeverity('');
  };

  const getUnresolvedObs = (report: FollowUpReport) =>
    report.observations.filter((o) => o.status !== 'Closed');

  const getOverdueCount = (report: FollowUpReport) => {
    const today = new Date().toISOString().split('T')[0];
    return report.observations.filter((o) => o.status !== 'Closed' && o.dueDate < today).length;
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-white sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Open Inspections Tracker</h1>
            <p className="text-xs text-muted mt-0.5">Vessels with unresolved inspection items — filterable by owner and ship manager</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted">Sort by:</label>
            <select
              className="input-field text-xs w-auto"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            >
              <option value="openCount">Most open items</option>
              <option value="vesselName">Vessel name</option>
              <option value="inspectionDate">Inspection date</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 py-5 space-y-4 max-w-screen-2xl mx-auto w-full">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Vessels with Open Items', value: summaryStats.vessels, color: 'hsl(var(--primary))' },
            { label: 'Total Open Items', value: summaryStats.totalOpen, color: '#dc2626' },
            { label: 'In Progress Items', value: summaryStats.totalInProgress, color: '#d97706' },
            { label: 'Critical Unresolved', value: summaryStats.criticalOpen, color: '#7c2d12' },
          ].map(({ label, value, color }) => (
            <div key={`stat-${label}`} className="bg-white rounded-xl border p-4 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
              <p className="text-xs text-muted mb-1">{label}</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="bg-white rounded-xl border shadow-card p-4" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                className="input-field pl-8 text-sm"
                placeholder="Search vessel, IMO, owner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className={`btn-secondary text-xs flex items-center gap-2 ${activeFilterCount > 0 ? 'border-primary text-primary' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} /> Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button className="text-xs text-muted hover:text-destructive flex items-center gap-1" onClick={clearFilters}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-3" style={{ borderColor: 'hsl(var(--border))' }}>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Owner</label>
                <select className="input-field text-xs" value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
                  <option value="">All owners</option>
                  {uniqueOwners.map((o) => <option key={`fo-${o}`} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Ship Manager</label>
                <select className="input-field text-xs" value={filterManager} onChange={(e) => setFilterManager(e.target.value)}>
                  <option value="">All managers</option>
                  {uniqueManagers.map((m) => <option key={`fm-${m}`} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Has Unresolved Severity</label>
                <select className="input-field text-xs" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
                  <option value="">Any severity</option>
                  {['Critical', 'Severe', 'Moderate', 'Minor'].map((s) => (
                    <option key={`fs-${s}`} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Vessel Cards */}
        <div className="space-y-3">
          {filtered.map((report) => {
            const isExpanded = expandedId === report.id;
            const unresolved = getUnresolvedObs(report);
            const overdueCount = getOverdueCount(report);
            const criticalCount = unresolved.filter((o) => o.severity === 'Critical').length;
            const severeCount = unresolved.filter((o) => o.severity === 'Severe').length;

            return (
              <div key={`vessel-${report.id}`} className="bg-white rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
                {/* Card Header */}
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                        <Ship size={18} style={{ color: 'hsl(var(--primary))' }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-sm">{report.vesselName}</h3>
                          <span className="text-xs text-muted font-mono">IMO {report.imoNumber}</span>
                          {overdueCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 flex items-center gap-1">
                              <AlertCircle size={10} /> {overdueCount} overdue
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-muted">{report.owner}</span>
                          <span className="text-xs text-muted">·</span>
                          <span className="text-xs text-muted">{report.manager}</span>
                          <span className="text-xs text-muted">·</span>
                          <span className="text-xs text-muted">Inspected {report.inspectionDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Severity badges */}
                      {criticalCount > 0 && (
                        <span className="px-2 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700">
                          {criticalCount} Critical
                        </span>
                      )}
                      {severeCount > 0 && (
                        <span className="px-2 py-1 rounded-lg text-xs font-bold bg-orange-100 text-orange-700">
                          {severeCount} Severe
                        </span>
                      )}
                      <button
                        className="btn-secondary text-xs flex items-center gap-1"
                        onClick={() => setExpandedId(isExpanded ? null : report.id)}
                      >
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {unresolved.length} unresolved
                      </button>
                    </div>
                  </div>

                  {/* Status mini-bar */}
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                      {(['Open', 'In Progress', 'Closed'] as ObservationStatus[]).map((s) => {
                        const count = report.observations.filter((o) => o.status === s).length;
                        const pct = report.totalObservations > 0 ? (count / report.totalObservations) * 100 : 0;
                        return pct > 0 ? (
                          <div
                            key={`bar-${s}`}
                            className="h-full transition-all"
                            style={{ width: `${pct}%`, background: STATUS_BAR_COLOR[s] }}
                            title={`${s}: ${count}`}
                          />
                        ) : null;
                      })}
                    </div>
                    <div className="flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="text-red-600 font-medium">{report.openCount} Open</span>
                      <span className="text-amber-600 font-medium">{report.inProgressCount} In Progress</span>
                      <span className="text-green-600 font-medium">{report.closedCount} Closed</span>
                    </div>
                  </div>
                </div>

                {/* Expanded: Unresolved Items Table */}
                {isExpanded && (
                  <div className="border-t overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
                          {['Ref', 'Category', 'Observation', 'Severity', 'LSD', 'Status', 'Due Date', 'Corrective Action'].map((h) => (
                            <th key={`th-${h}`} className="px-3 py-2.5 text-left font-semibold text-muted whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {unresolved
                          .sort((a, b) => {
                            const order = { Critical: 0, Severe: 1, Moderate: 2, Minor: 3, Normal: 4 };
                            return (order[a.severity] ?? 5) - (order[b.severity] ?? 5);
                          })
                          .map((obs) => {
                            const today = new Date().toISOString().split('T')[0];
                            const isOverdue = obs.dueDate < today;
                            return (
                              <tr key={`obs-${obs.id}`} className={`border-b transition-colors ${isOverdue ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}
                                style={{ borderColor: 'hsl(var(--border))' }}>
                                <td className="px-3 py-2.5 font-mono font-bold text-primary whitespace-nowrap">{obs.ref}</td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                  <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-700">{obs.category}</span>
                                </td>
                                <td className="px-3 py-2.5 max-w-xs">
                                  <p className="truncate" title={obs.description}>{obs.description}</p>
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                    obs.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                                    obs.severity === 'Severe' ? 'bg-orange-100 text-orange-700' :
                                    obs.severity === 'Moderate'? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'
                                  }`}>{obs.severity}</span>
                                </td>
                                <td className="px-3 py-2.5 font-mono font-bold whitespace-nowrap">{obs.lsdScore}</td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    obs.status === 'Open' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                                  }`}>
                                    {obs.status === 'Open' ? <AlertCircle size={10} /> : <Clock size={10} />}
                                    {obs.status}
                                  </span>
                                </td>
                                <td className={`px-3 py-2.5 whitespace-nowrap font-medium ${isOverdue ? 'text-red-600' : 'text-muted'}`}>
                                  {obs.dueDate}
                                  {isOverdue && <span className="ml-1 text-red-500">⚠</span>}
                                </td>
                                <td className="px-3 py-2.5 max-w-xs">
                                  {obs.correctiveAction ? (
                                    <p className="truncate text-green-700" title={obs.correctiveAction}>{obs.correctiveAction}</p>
                                  ) : (
                                    <span className="text-muted italic">Awaiting owner</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: 'hsl(var(--border))' }}>
              <Ship size={32} className="mx-auto text-muted mb-3" />
              <p className="font-semibold text-sm">No vessels with open inspection items</p>
              <p className="text-xs text-muted mt-1">All inspections are closed, or adjust your filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
